import axios from "axios";
import { CreateTaskDto, UpdateTaskStatusDto, UpdateTaskDueDateDto, GenerateTasksFromChecklistDto } from "../interfaces/dto/TaskDto";

export const createTask = (taskData: CreateTaskDto) => {
    // Transform the data to match backend model
    const backendModel = {
        Description: taskData.description,
        DueDate: taskData.dueDate || new Date(),
        Priority: taskData.priority,
        AssignedToUserId: taskData.assignedToUserId,
        ProjectId: taskData.projectId,
        ChecklistDefinitionId: taskData.checklistDefinitionId,
        ChecklistPositionId: taskData.checklistPositionId
    };
    
    return axios.post("/task", backendModel);
};

export const createTaskWithImages = (taskData: CreateTaskDto, images: File[]) => {
    const formData = new FormData();
    
    // Add task data
    formData.append("Description", taskData.description);
    formData.append("DueDate", (taskData.dueDate || new Date()).toISOString());
    formData.append("Priority", taskData.priority.toString());
    formData.append("AssignedToUserId", taskData.assignedToUserId);
    formData.append("ProjectId", taskData.projectId.toString());
    
    if (taskData.checklistDefinitionId) {
        formData.append("ChecklistDefinitionId", taskData.checklistDefinitionId.toString());
    }
    if (taskData.checklistPositionId) {
        formData.append("ChecklistPositionId", taskData.checklistPositionId.toString());
    }
    
    // Add images
    images.forEach((image, index) => {
        formData.append("images", image);
    });

    return axios.post("/task/with-images", formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const getMyTasks = (projectId?: number) => {
    const params = projectId ? { projectId } : {};
    return axios.get("/Task/GetMyTasks", { params });
};

export const getTaskById = (taskId: number) => {
    return axios.get(`/Task/GetTask?taskId=${taskId}`);
};

export const updateTaskStatus = (taskId: number, statusData: UpdateTaskStatusDto) => {
    return axios.put(`/task/${taskId}/status`, {
        TaskId: taskId,
        Status: statusData.status
    });
};

export const updateTaskDueDate = (taskId: number, dueDateData: UpdateTaskDueDateDto) => {
    return axios.put(`/Task/UpdateTaskDueDate`, { 
        TaskId: taskId, 
        NewDueDate: dueDateData.dueDate 
    });
};

export const deleteTask = (taskId: number) => {
    return axios.delete(`/Task/DeleteTask?taskId=${taskId}`);
};

export const addImageToTask = (taskId: number, imageFile: File) => {
    const formData = new FormData();
    formData.append("imageFile", imageFile);
    return axios.post(`/Task/AddImageToTask?taskId=${taskId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const generateTasksFromChecklist = (data: GenerateTasksFromChecklistDto) => {
    // Transform the data to match backend model
    const backendModel = {
        ChecklistDefinitionId: data.checklistDefinitionId,
        DueDate: data.dueDate ? data.dueDate.toISOString() : "",
        AssignedToUserId: data.assignedToUserId
    };
    
    return axios.post("/task/generate-from-checklist", backendModel);
};

export const getTasksByProject = (projectId: number) => {
    return axios.get("/Task/GetProjectTasks", { params: { projectId } });
};

export const getOverdueTasks = () => {
    return axios.get("/Task/GetOverdueTasks");
};

export const getApproachingDeadlineTasks = (days: number = 3) => {
    return axios.get("/Task/GetTasksApproachingDeadline", { params: { daysAhead: days } });
};

export const getSubTasks = (taskId: number) => {
    return axios.get(`/task/${taskId}/subtasks`);
};

export const createSubTask = (taskId: number, description: string) => {
    return axios.post(`/task/${taskId}/subtasks`, { description });
};

export const createSubTaskWithImages = (taskId: number, description: string, images: File[]) => {
    const formData = new FormData();
    formData.append("description", description);
    images.forEach(image => {
        formData.append("images", image);
    });
    return axios.post(`/task/${taskId}/subtasks/with-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

export const updateSubTask = (subTaskId: number, description: string) => {
    return axios.put(`/task/subtasks/${subTaskId}`, { description });
};

export const updateSubTaskDone = (subTaskId: number, isDone: boolean) => {
    return axios.patch(`/task/subtasks/${subTaskId}/done`, { isDone });
};

export const deleteSubTask = (subTaskId: number) => {
    return axios.delete(`/task/subtasks/${subTaskId}`);
};
